import type { ReactNode } from 'react';

import { PUBLIC_MOBILE_APP } from '../../config/publicMobileApp';
import '../../styles/publicStoreBadges.css';

type Props = {
  className?: string;
  theme?: 'dark' | 'light';
};

function GooglePlayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M1.5 1.2c-.3.2-.5.5-.5.9v19.8c0 .4.2.7.5.9l.1.1 11.2-11.2V12L1.6 1.1l-.1.1zm13.2 10.5 2.8-2.8L3.2 2.4l11.5 9.3zm3.6-3.4 2.4 1.4c.7.4.7 1.4 0 1.8l-2.4 1.4-2.6-2.6 2.6-2zm-13.7-1.7 9.7 7.8-2.8 2.8L4.6 6.6z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

type BadgeProps = {
  eyebrow: string;
  label: string;
  icon: ReactNode;
  href?: string;
  ariaLabel: string;
};

function StoreBadge({ eyebrow, label, icon, href, ariaLabel }: BadgeProps) {
  const content = (
    <>
      <span className="public-store-badge__icon">{icon}</span>
      <span className="public-store-badge__text">
        <span className="public-store-badge__eyebrow">{eyebrow}</span>
        <span className="public-store-badge__label">{label}</span>
      </span>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="public-store-badge"
        aria-label={ariaLabel}
      >
        {content}
      </a>
    );
  }

  return (
    <span className="public-store-badge public-store-badge--disabled" aria-label={ariaLabel}>
      {content}
    </span>
  );
}

export default function PublicStoreBadges({ className = '', theme = 'dark' }: Props) {
  const themeClass = theme === 'light' ? 'public-store-badges--light' : 'public-store-badges--dark';

  return (
    <div className={`public-store-badges ${themeClass} ${className}`.trim()}>
      <StoreBadge
        eyebrow="Disponible sur"
        label="Google Play"
        icon={<GooglePlayIcon />}
        href={PUBLIC_MOBILE_APP.playStoreUrl || undefined}
        ariaLabel={
          PUBLIC_MOBILE_APP.playStoreUrl
            ? 'Télécharger sur Google Play'
            : 'Disponible sur Google Play'
        }
      />
      <StoreBadge
        eyebrow="Disponible sur"
        label="l'App Store"
        icon={<AppleIcon />}
        href={PUBLIC_MOBILE_APP.appStoreUrl || undefined}
        ariaLabel={
          PUBLIC_MOBILE_APP.appStoreUrl
            ? "Télécharger sur l'App Store"
            : "Disponible sur l'App Store"
        }
      />
    </div>
  );
}
